import React, { useEffect, useRef, useState } from 'react';
import {
    Dialog,
    DialogContent,
} from 'components/ui/dialog';
import { Search } from 'lucide-react';
import LangService from 'services/LangService';
import { useCommunity } from 'context/community';
import { cn } from 'utils/cn';

const MIN_QUERY = 2;
const DEBOUNCE_MS = 250;
const RESULT_CHUNK = 30;
const SCROLL_END_PX = 24;

const EMPTY_RESULTS = {
    members: [],
    events: [],
    jobs: [],
    membersHasMore: false,
    jobsHasMore: false,
    membersError: false,
};

const ResultThumb = ({ kind, image, label }) => {
    const initials = (label || '?').slice(0, 2).toUpperCase();
    const shape = kind === 'person' ? 'rounded-full' : 'rounded-md';

    if (!image) {
        return (
            <span className={cn(
                'h-9 w-9 bg-slate-100 flex items-center justify-center text-[11px] font-semibold text-slate-600 shrink-0',
                shape
            )}>
                {initials}
            </span>
        );
    }

    return (
        <img
            src={image}
            alt={label}
            className={cn(
                'h-9 w-9 bg-slate-100 shrink-0',
                shape,
                kind === 'job' ? 'object-contain p-0.5' : 'object-cover'
            )}
        />
    );
};

const ResultGroup = ({ title, items, kind, listId, onSelect, hasMore, loadingMore, loadingLabel, onLoadMore }) => {
    const scrollerRef = useRef(null);

    const maybeLoadMore = () => {
        const el = scrollerRef.current;
        if (!el || !hasMore || loadingMore || !onLoadMore || el.clientHeight === 0) {
            return;
        }
        const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
        if (remaining <= SCROLL_END_PX) {
            onLoadMore();
        }
    };

    useEffect(() => {
        maybeLoadMore();
    }, [hasMore, items.length, loadingMore]);

    if (!items?.length) {
        return null;
    }

    return (
        <div>
            <div className="px-2 py-1 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                {title}
            </div>
            <div
                ref={scrollerRef}
                data-testid={listId}
                className="max-h-48 overflow-y-auto"
                onScroll={maybeLoadMore}
            >
                {items.map((item) => (
                    <button
                        key={item.id || item.to}
                        type="button"
                        onClick={() => onSelect(item.to)}
                        className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-foreground hover:bg-[color-mix(in_srgb,var(--primary-color)_12%,transparent)] text-left transition-colors"
                    >
                        <ResultThumb kind={kind} image={item.image} label={item.label} />
                        <span className="min-w-0 flex-1">
                            <span className="block font-medium truncate">{item.label}</span>
                            {item.subtitle ? (
                                <span className="block text-[11px] text-muted-foreground truncate">{item.subtitle}</span>
                            ) : null}
                        </span>
                    </button>
                ))}
                {loadingMore ? (
                    <p className="px-3 py-2 text-xs text-muted-foreground">{loadingLabel}</p>
                ) : null}
            </div>
        </div>
    );
};

const DEFAULT_SEARCH_SCOPES = { members: true, events: true, jobs: true };

export const NavCommandSearch = ({
    onSearch,
    onNavigate,
    searchScopes = DEFAULT_SEARCH_SCOPES,
    compact = false,
    className,
}) => {
    const [open, setOpen] = useState(false);
    const [keyboardFrame, setKeyboardFrame] = useState(null);
    const [query, setQuery] = useState('');
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState(EMPTY_RESULTS);
    const [eventsLimit, setEventsLimit] = useState(RESULT_CHUNK);
    const [membersNextPage, setMembersNextPage] = useState(0);
    const [jobsNextPage, setJobsNextPage] = useState(0);
    const [loadingMoreKind, setLoadingMoreKind] = useState(null);
    const requestId = useRef(0);
    const loadingMoreRef = useRef({ members: false, jobs: false });
    const activeQueryRef = useRef('');

    useEffect(() => {
        if (!open) {
            setKeyboardFrame(null);
            return undefined;
        }
        const viewport = window.visualViewport;
        if (!viewport) {
            return undefined;
        }
        const update = () => {
            const keyboardHeight = window.innerHeight - viewport.height;
            if (keyboardHeight < 80) {
                setKeyboardFrame(null);
                return;
            }
            setKeyboardFrame({
                top: viewport.offsetTop + viewport.height / 2,
                maxHeight: Math.max(160, viewport.height - 24),
            });
        };
        update();
        viewport.addEventListener('resize', update);
        viewport.addEventListener('scroll', update);
        return () => {
            viewport.removeEventListener('resize', update);
            viewport.removeEventListener('scroll', update);
        };
    }, [open]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
                e.preventDefault();
                setOpen((prev) => !prev);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    useEffect(() => {
        const gen = ++requestId.current;
        const trimmed = query.trim();
        if (!open || trimmed.length < MIN_QUERY || !onSearch) {
            setResults(EMPTY_RESULTS);
            setEventsLimit(RESULT_CHUNK);
            setLoading(false);
            setLoadingMoreKind(null);
            return undefined;
        }

        setLoading(true);
        const timer = setTimeout(async () => {
            try {
                const next = await onSearch(trimmed);
                if (requestId.current !== gen) {
                    return;
                }
                const members = next?.members || [];
                const jobs = next?.jobs || [];
                setResults({
                    members,
                    events: next?.events || [],
                    jobs,
                    membersHasMore: Boolean(next?.membersHasMore) && members.length > 0,
                    jobsHasMore: Boolean(next?.jobsHasMore) && jobs.length > 0,
                    membersError: Boolean(next?.membersError),
                });
                setMembersNextPage(next?.membersNextPage ?? 0);
                setJobsNextPage(next?.jobsNextPage ?? 0);
                setEventsLimit(RESULT_CHUNK);
                activeQueryRef.current = trimmed;
            } catch (e) {
                if (requestId.current === gen) {
                    setResults(EMPTY_RESULTS);
                }
            } finally {
                if (requestId.current === gen) {
                    setLoading(false);
                }
            }
        }, DEBOUNCE_MS);

        return () => {
            clearTimeout(timer);
        };
    }, [query, open, onSearch]);

    const loadMore = async (kind) => {
        const trimmed = query.trim();
        if (!onSearch || loading || trimmed !== activeQueryRef.current || loadingMoreRef.current[kind]) {
            return;
        }
        if (kind === 'events') {
            setEventsLimit((current) => current + RESULT_CHUNK);
            return;
        }

        const gen = requestId.current;
        const page = kind === 'members' ? membersNextPage : jobsNextPage;
        loadingMoreRef.current[kind] = true;
        setLoadingMoreKind(kind);
        try {
            const next = await onSearch(trimmed, kind === 'members'
                ? { membersPage: page, singlePage: true, events: false, jobs: false }
                : { jobsPage: page, events: false, members: false });
            if (requestId.current !== gen) {
                return;
            }
            if (kind === 'members') {
                const more = next?.members || [];
                setResults((prev) => ({
                    ...prev,
                    members: [...prev.members, ...more],
                    membersHasMore: Boolean(next?.membersHasMore) && more.length > 0,
                }));
                setMembersNextPage(next?.membersNextPage ?? page);
            } else {
                const more = next?.jobs || [];
                setResults((prev) => ({
                    ...prev,
                    jobs: [...prev.jobs, ...more],
                    jobsHasMore: Boolean(next?.jobsHasMore) && more.length > 0,
                }));
                setJobsNextPage(next?.jobsNextPage ?? page);
            }
        } catch (e) {
            if (requestId.current === gen) {
                setResults((prev) => ({
                    ...prev,
                    ...(kind === 'members' ? { membersHasMore: false } : { jobsHasMore: false }),
                }));
            }
        } finally {
            if (requestId.current === gen) {
                loadingMoreRef.current[kind] = false;
                setLoadingMoreKind(null);
            }
        }
    };

    const handleSelect = (to) => {
        setOpen(false);
        setQuery('');
        if (onNavigate) onNavigate(to);
    };

    const { community } = useCommunity();
    const communityName = community?.title || community?.name || community?.domain || 'Joinee';
    const copy = LangService.map.components.commandSearch;
    const placeholder = copy.placeholder(communityName);
    const hint = copy.hint(searchScopes);
    const trimmed = query.trim();
    const hasQuery = trimmed.length >= MIN_QUERY;
    const visibleEvents = results.events.slice(0, eventsLimit);
    const hasHits = results.members.length + results.events.length + results.jobs.length > 0;
    const membersFailed = Boolean(results.membersError);

    return (
        <>
            <button
                type="button"
                onClick={() => setOpen(true)}
                className={cn(
                    'group flex items-center gap-2 px-3 py-1.5 rounded-lg border border-border bg-muted/70 text-muted-foreground hover:text-foreground hover:bg-accent text-xs transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    compact ? 'w-9 h-9 p-0 justify-center' : 'w-48 justify-between',
                    className
                )}
                aria-label={copy.search}
            >
                <div className="flex items-center gap-2">
                    <Search className="w-3.5 h-3.5 text-muted-foreground group-hover:text-foreground shrink-0" />
                    {!compact && <span>{placeholder}</span>}
                </div>
                {!compact && (
                    <kbd className="hidden sm:inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground bg-card rounded border border-border">
                        ⌘K
                    </kbd>
                )}
            </button>

            <Dialog open={open} onOpenChange={(nextOpen) => {
                setOpen(nextOpen);
                if (!nextOpen) {
                    setQuery('');
                    setResults(EMPTY_RESULTS);
                    setEventsLimit(RESULT_CHUNK);
                }
            }}>
                <DialogContent
                    className="flex w-[calc(100%-30px)] max-w-xl flex-col overflow-hidden border-border bg-card p-0 shadow-2xl"
                    style={keyboardFrame ? { top: keyboardFrame.top, maxHeight: keyboardFrame.maxHeight } : undefined}
                >
                    <div className="flex shrink-0 items-center border-b border-border px-4">
                        <Search className="w-4 h-4 text-muted-foreground shrink-0" />
                        <input
                            type="text"
                            placeholder={hint}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            className="w-full h-12 px-3 bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
                            autoFocus
                        />
                    </div>

                    <div className="min-h-0 max-h-[28rem] space-y-2 overflow-y-auto p-2">
                        {!hasQuery ? (
                            <p className="px-2 py-6 text-center text-sm text-muted-foreground">
                                {hint}
                            </p>
                        ) : loading ? (
                            <p className="px-2 py-6 text-center text-sm text-muted-foreground">
                                {copy.searching}
                            </p>
                        ) : !hasHits && !membersFailed ? (
                            <p className="px-2 py-6 text-center text-sm text-muted-foreground">
                                {copy.noResults(trimmed)}
                            </p>
                        ) : (
                            <>
                                {membersFailed ? (
                                    <p
                                        className="px-2 py-3 text-sm text-rose-500"
                                        data-testid="command-search-members-error"
                                    >
                                        {copy.membersError || 'Could not load members.'}
                                    </p>
                                ) : (
                                    <ResultGroup
                                        title={copy.members}
                                        kind="person"
                                        listId="command-search-members"
                                        items={results.members}
                                        onSelect={handleSelect}
                                        hasMore={results.membersHasMore}
                                        loadingMore={loadingMoreKind === 'members'}
                                        loadingLabel={copy.searching}
                                        onLoadMore={() => loadMore('members')}
                                    />
                                )}
                                <ResultGroup
                                    title={copy.events}
                                    kind="event"
                                    listId="command-search-events"
                                    items={visibleEvents}
                                    onSelect={handleSelect}
                                    hasMore={eventsLimit < results.events.length}
                                    loadingLabel={copy.searching}
                                    onLoadMore={() => loadMore('events')}
                                />
                                <ResultGroup
                                    title={copy.career}
                                    kind="job"
                                    listId="command-search-jobs"
                                    items={results.jobs}
                                    onSelect={handleSelect}
                                    hasMore={results.jobsHasMore}
                                    loadingMore={loadingMoreKind === 'jobs'}
                                    loadingLabel={copy.searching}
                                    onLoadMore={() => loadMore('jobs')}
                                />
                            </>
                        )}
                    </div>

                    <div className="flex shrink-0 items-center justify-between border-t border-border bg-muted px-4 py-2 text-[11px] text-muted-foreground">
                        <span>{copy.palette}</span>
                        <div className="flex items-center gap-2">
                            <span>{copy.press}</span>
                            <kbd className="px-1.5 py-0.5 bg-card rounded border border-border text-[10px]">ESC</kbd>
                            <span>{copy.toClose}</span>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};
